import React, { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Rocket, ChevronRight, ChevronLeft, Plus, Trash2, Lock, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from './index';
import { maakB2bProject, type B2bProject, type PoolProfessional, type WizardMijlpaalInvoer } from '../services/lopendeTrajectenApi';

/**
 * Project-Onboarding Wizard (Long-term B2B Project Engine, migratie 136).
 *
 *  Stap 1  Project- & partnerselectie   — professional uit de pool, naam, tijdas
 *  Stap 2  Mijlpalen & uren-caps        — opeenvolgende fasen met harde cap
 *  Stap 3  Financiële verzegeling       — cap × pool-uurtarief = maximale euro-waarde
 *          [🚀 Activeer Project & Mijlpaal 1] → M1 ACTIVE_RUNNING, rest LOCKED_PENDING
 */

const euro = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' });
const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';
const labelClass = 'block text-xs font-medium text-gray-700 mb-1';
const vandaag = () => new Date().toISOString().slice(0, 10);

const STAPPEN = [
  { nr: 1, label: 'Project & partner' },
  { nr: 2, label: 'Mijlpalen & uren-caps' },
  { nr: 3, label: 'Financiële verzegeling' },
];

interface MijlpaalRij {
  titel: string;
  cap: string;
}

export const B2bProjectWizard: React.FC<{ pool: PoolProfessional[]; onKlaar: (project: B2bProject) => void; onAnnuleren: () => void }> = ({
  pool,
  onKlaar,
  onAnnuleren,
}) => {
  const [stap, setStap] = useState<1 | 2 | 3>(1);
  const [professionalId, setProfessionalId] = useState(pool[0]?.professional_id ?? '');
  const [titel, setTitel] = useState('');
  const [omschrijving, setOmschrijving] = useState('');
  const [start, setStart] = useState(vandaag());
  const [eind, setEind] = useState('');
  const [mijlpalen, setMijlpalen] = useState<MijlpaalRij[]>([
    { titel: 'Intakefase & audits', cap: '' },
    { titel: '', cap: '' },
    { titel: 'Eindevaluatie & overdracht', cap: '' },
  ]);

  const prof = pool.find((p) => p.professional_id === professionalId) ?? null;
  const tarief = prof?.uurtarief ?? null;

  const stap1Geldig = Boolean(prof) && tarief != null && titel.trim().length >= 3 && Boolean(start) && (!eind || eind >= start);
  const geldigeMijlpalen = useMemo(
    () => mijlpalen.map((m) => ({ titel: m.titel.trim(), cap_uren: parseFloat(m.cap) })).filter((m) => m.titel.length >= 3 && m.cap_uren > 0),
    [mijlpalen]
  );
  const stap2Geldig = mijlpalen.length > 0 && geldigeMijlpalen.length === mijlpalen.length;

  const verzegeling = useMemo(
    () => geldigeMijlpalen.map((m) => ({ ...m, max_bedrag: tarief == null ? 0 : Math.round(m.cap_uren * tarief * 100) / 100 })),
    [geldigeMijlpalen, tarief]
  );
  const totaalUren = verzegeling.reduce((s, m) => s + m.cap_uren, 0);
  const totaalBedrag = verzegeling.reduce((s, m) => s + m.max_bedrag, 0);

  const activeer = useMutation({
    mutationFn: () =>
      maakB2bProject({
        professional_id: professionalId,
        titel: titel.trim(),
        omschrijving: omschrijving.trim() || null,
        start_datum: start,
        verwachte_eind_datum: eind || null,
        mijlpalen: geldigeMijlpalen as WizardMijlpaalInvoer[],
      }),
    onSuccess: (project) => {
      toast.success(`Project geactiveerd — Mijlpaal 1 staat op ACTIVE_RUNNING`);
      onKlaar(project);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Activeren mislukt'),
  });

  const zetMijlpaal = (i: number, patch: Partial<MijlpaalRij>) => setMijlpalen((lijst) => lijst.map((m, j) => (j === i ? { ...m, ...patch } : m)));

  return (
    <div className="rounded-xl border border-blue-200 bg-white shadow-sm overflow-hidden">
      {/* Stepper */}
      <div className="px-4 py-3 bg-blue-50 border-b border-blue-200 flex flex-wrap items-center gap-2">
        <Rocket className="w-4 h-4 text-blue-700" />
        <span className="font-semibold text-blue-900 text-sm">Project-Onboarding Wizard</span>
        <ol className="ml-auto flex items-center gap-1 text-xs">
          {STAPPEN.map((s, i) => (
            <li key={s.nr} className="flex items-center gap-1">
              <span
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${
                  stap === s.nr ? 'bg-blue-600 text-white' : stap > s.nr ? 'bg-green-100 text-green-800' : 'bg-white border border-gray-200 text-gray-500'
                }`}
              >
                <span className="font-semibold">{s.nr}</span> {s.label}
              </span>
              {i < STAPPEN.length - 1 && <ChevronRight className="w-3 h-3 text-gray-400" />}
            </li>
          ))}
        </ol>
      </div>

      <div className="p-4 space-y-4">
        {/* ── Stap 1 ── */}
        {stap === 1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className={labelClass}>Professional uit uw pool (Professional Picker) *</label>
              <select className={inputClass} value={professionalId} onChange={(e) => setProfessionalId(e.target.value)}>
                {pool.map((p) => (
                  <option key={p.professional_id} value={p.professional_id}>
                    {p.naam} — {p.discipline_label}
                  </option>
                ))}
              </select>
              {prof && (
                <div className={`text-xs mt-1 ${tarief == null ? 'text-red-700' : 'text-gray-500'}`}>
                  {tarief == null
                    ? 'Geen pool-uurtarief vastgelegd — leg eerst een tarief vast in de Pool-Configurator; anders kan het budget niet worden verzegeld.'
                    : `Gekoppeld pool-uurtarief: ${euro.format(tarief)} per uur`}
                </div>
              )}
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Projectnaam *</label>
              <input className={inputClass} value={titel} onChange={(e) => setTitel(e.target.value)} placeholder='bijv. "Cultuurtraject & Werkdrukreductie Vestiging Eindhoven"' />
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Toelichting (optioneel)</label>
              <textarea className={inputClass} rows={2} value={omschrijving} onChange={(e) => setOmschrijving(e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Startdatum *</label>
              <input type="date" className={inputClass} value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Verwachte einddatum</label>
              <input type="date" className={inputClass} value={eind} min={start} onChange={(e) => setEind(e.target.value)} />
              {eind && eind < start && <div className="text-xs text-red-700 mt-1">Einddatum ligt vóór de startdatum.</div>}
            </div>
          </div>
        )}

        {/* ── Stap 2 ── */}
        {stap === 2 && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Knip het project op in opeenvolgende fasen. De professional bepaalt vooraf de benodigde uren per mijlpaal; u voert hier de <strong>harde cap</strong> in.
              Boven de cap blokkeert de urenregistratie automatisch.
            </p>
            <div className="space-y-2">
              {mijlpalen.map((m, i) => (
                <div key={i} className="grid grid-cols-[auto_1fr_140px_auto] items-center gap-2">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-semibold ${i === 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    {i === 0 ? i + 1 : <><Lock className="w-3 h-3 mr-0.5" />{i + 1}</>}
                  </span>
                  <input
                    className={inputClass}
                    value={m.titel}
                    onChange={(e) => zetMijlpaal(i, { titel: e.target.value })}
                    placeholder={`Mijlpaal ${i + 1}: bijv. Intervisiesessies & leidinggevenden-coaching`}
                  />
                  <div className="relative">
                    <input
                      type="number"
                      min="0.5"
                      step="0.5"
                      className={`${inputClass} pr-10`}
                      value={m.cap}
                      onChange={(e) => zetMijlpaal(i, { cap: e.target.value })}
                      placeholder="cap"
                    />
                    <span className="absolute right-3 top-2 text-xs text-gray-400">uur</span>
                  </div>
                  <button
                    type="button"
                    className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-30"
                    disabled={mijlpalen.length <= 1}
                    onClick={() => setMijlpalen((l) => l.filter((_, j) => j !== i))}
                    title="Mijlpaal verwijderen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <Button size="sm" variant="outline" onClick={() => setMijlpalen((l) => [...l, { titel: '', cap: '' }])} disabled={mijlpalen.length >= 12}>
              <Plus className="w-4 h-4 mr-1" /> Mijlpaal toevoegen
            </Button>
            {!stap2Geldig && <div className="text-xs text-orange-700">Elke mijlpaal heeft een naam (min. 3 tekens) en een uren-cap groter dan nul nodig.</div>}
          </div>
        )}

        {/* ── Stap 3 ── */}
        {stap === 3 && (
          <div className="space-y-3">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-900">
              <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                <strong>Financiële verzegeling:</strong> per mijlpaal wordt de maximale euro-waarde berekend als cap × pool-uurtarief ({tarief == null ? '—' : euro.format(tarief)}/u).
                Bij accordering wordt uitsluitend de <em>werkelijk geschreven</em> tijd gefactureerd: 100% gaat naar de professional, BeLagom rekent daarbovenop 15% platformfee.
              </span>
            </div>
            <div className="text-sm">
              <div className="font-semibold text-gray-900">{titel}</div>
              <div className="text-gray-600">
                {prof?.naam} · {prof?.discipline_label} · {new Date(start).toLocaleDateString('nl-NL')}
                {eind ? ` → ${new Date(eind).toLocaleDateString('nl-NL')}` : ''}
              </div>
            </div>
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Mijlpaal</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase">Harde cap</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase">Berekening</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 uppercase">Maximaal</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase">Startstatus</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {verzegeling.map((m, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2">
                      <span className="text-gray-500">Mijlpaal {i + 1}:</span> {m.titel}
                    </td>
                    <td className="px-3 py-2 text-right">{m.cap_uren} uur</td>
                    <td className="px-3 py-2 text-right text-gray-500">
                      {m.cap_uren} × {tarief == null ? '—' : euro.format(tarief)}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold">{euro.format(m.max_bedrag)}</td>
                    <td className="px-3 py-2">
                      {i === 0 ? (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">ACTIVE_RUNNING</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          <Lock className="w-3 h-3" /> LOCKED_PENDING
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td className="px-3 py-2 font-semibold">Totaal verzegeld budget</td>
                  <td className="px-3 py-2 text-right font-semibold">{totaalUren} uur</td>
                  <td />
                  <td className="px-3 py-2 text-right font-semibold">{euro.format(totaalBedrag)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Navigatie */}
        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
          {stap > 1 ? (
            <Button size="sm" variant="outline" onClick={() => setStap((s) => (s - 1) as 1 | 2 | 3)}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Vorige
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={onAnnuleren}>
              Annuleren
            </Button>
          )}
          <div className="ml-auto flex gap-2">
            {stap === 1 && (
              <Button size="sm" onClick={() => setStap(2)} disabled={!stap1Geldig}>
                Volgende: mijlpalen <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
            {stap === 2 && (
              <Button size="sm" onClick={() => setStap(3)} disabled={!stap2Geldig}>
                Volgende: verzegeling <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
            {stap === 3 && (
              <Button size="sm" className="bg-green-600 hover:bg-green-700 border-green-600" onClick={() => activeer.mutate()} disabled={activeer.isPending || !stap1Geldig || !stap2Geldig}>
                {activeer.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Rocket className="w-4 h-4 mr-1" /> Activeer Project &amp; Mijlpaal 1</>}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default B2bProjectWizard;
